import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Bell } from "lucide-react";
import { PickupRequestCard } from "./PickupRequestCard";
import { DenyPickupDialog } from "./DenyPickupDialog";
import type { UserPickup } from "@/types/database";

interface PackageInfo {
  id: string;
  retailer: string;
  size: string;
}

interface PickupWithPackages extends UserPickup {
  packages: PackageInfo[];
}

interface PickupRequestsSectionProps {
  userId: string;
  subscriptionType: string | null;
}

export const PickupRequestsSection = ({
  userId,
  subscriptionType,
}: PickupRequestsSectionProps) => {
  const { toast } = useToast();
  const [pickups, setPickups] = useState<PickupWithPackages[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedPickupId, setSelectedPickupId] = useState<string | null>(null);
  const [confirmedPickupsThisMonth, setConfirmedPickupsThisMonth] = useState(0);

  // Calculate max pickups based on subscription
  const getMaxPickups = () => {
    if (!subscriptionType) return 1;
    // bi-weekly plan = 2 pickups, monthly = 1 pickup
    return subscriptionType.toLowerCase().includes("bi-weekly") ? 2 : 1;
  };

  const maxPickups = getMaxPickups();
  const remainingPickups = Math.max(0, maxPickups - confirmedPickupsThisMonth);

  const fetchPickupRequests = async () => {
    try {
      // Fetch user pickups with status awaiting_confirmation
      const { data: pickupData, error: pickupError } = await (supabase as any)
        .from("user_pickups")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "awaiting_confirmation")
        .order("created_at", { ascending: false });

      if (pickupError) throw pickupError;

      if (!pickupData || pickupData.length === 0) {
        setPickups([]);
        setLoading(false);
        return;
      }

      // Fetch packages linked to these pickups
      const pickupIds = pickupData.map((p: UserPickup) => p.id);
      const { data: packageData, error: packageError } = await (supabase as any)
        .from("packages")
        .select("id, retailer, size, pickup_id")
        .in("pickup_id", pickupIds);

      if (packageError) throw packageError;

      // Group packages by pickup
      const packagesByPickup = new Map<string, PackageInfo[]>();
      (packageData || []).forEach((pkg: any) => {
        const existing = packagesByPickup.get(pkg.pickup_id) || [];
        existing.push({ id: pkg.id, retailer: pkg.retailer, size: pkg.size });
        packagesByPickup.set(pkg.pickup_id, existing);
      });

      // Combine pickups with their packages
      const pickupsWithPackages: PickupWithPackages[] = pickupData.map((pickup: UserPickup) => ({
        ...pickup,
        packages: packagesByPickup.get(pickup.id) || [],
      }));

      setPickups(pickupsWithPackages);

      // Count pickups that count toward monthly limit (confirmed, completed, no_show)
      // Note: missed and cancelled do NOT count toward limit
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: countingPickups, error: confirmedError } = await (supabase as any)
        .from("user_pickups")
        .select("id, status")
        .eq("user_id", userId)
        .in("status", ["confirmed", "completed", "no_show"])
        .gte("updated_at", startOfMonth.toISOString());

      if (!confirmedError) {
        setConfirmedPickupsThisMonth(countingPickups?.length || 0);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching pickup requests:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPickupRequests();
  }, [userId]);

  const handleApprove = async (pickupId: string) => {
    // HARD ENFORCEMENT: Check if user has remaining pickups
    if (remainingPickups <= 0) {
      toast({
        title: "Pickup Limit Reached",
        description: "You've reached your pickup limit for this billing period. Please wait until your next billing cycle.",
        variant: "destructive",
      });
      return;
    }

    setActionLoading(true);
    try {
      // Double-check limit on backend before confirming
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: countingPickups } = await (supabase as any)
        .from("user_pickups")
        .select("id")
        .eq("user_id", userId)
        .in("status", ["confirmed", "completed", "no_show"])
        .gte("updated_at", startOfMonth.toISOString());

      const currentCount = countingPickups?.length || 0;
      if (currentCount >= maxPickups) {
        toast({
          title: "Pickup Limit Reached",
          description: "You've reached your pickup limit for this billing period.",
          variant: "destructive",
        });
        setActionLoading(false);
        return;
      }

      // Update pickup status to confirmed
      const { error } = await (supabase as any)
        .from("user_pickups")
        .update({ status: "confirmed" })
        .eq("id", pickupId);

      if (error) throw error;

      // Update linked packages status to scheduled
      await (supabase as any)
        .from("packages")
        .update({ status: "scheduled" })
        .eq("pickup_id", pickupId);

      toast({
        title: "Pickup Confirmed!",
        description: "Your pickup has been confirmed. We'll be there on the scheduled date.",
      });

      await fetchPickupRequests();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to confirm pickup",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyClick = (pickupId: string) => {
    setSelectedPickupId(pickupId);
    setDenyDialogOpen(true);
  };

  const handleDenyConfirm = async (data: {
    denialReason: string;
    counterProposedDate?: string;
    counterProposedTimeSlot?: string;
    userMessage?: string;
  }) => {
    if (!selectedPickupId) return;

    setActionLoading(true);
    try {
      const updateData: Record<string, any> = {
        status: data.counterProposedDate ? "counter_proposed" : "user_denied",
        denial_reason: data.denialReason,
        user_message: data.userMessage || null,
      };

      if (data.counterProposedDate && data.counterProposedTimeSlot) {
        updateData.counter_proposed_date = data.counterProposedDate;
        updateData.counter_proposed_time_slot = data.counterProposedTimeSlot;
      }

      const { error } = await (supabase as any)
        .from("user_pickups")
        .update(updateData)
        .eq("id", selectedPickupId);

      if (error) throw error;

      toast({
        title: data.counterProposedDate ? "Counter-Proposal Submitted" : "Request Submitted",
        description: data.counterProposedDate
          ? "We've received your alternative date/time proposal. We'll get back to you soon."
          : "We've received your request. Our team will reschedule your pickup.",
      });

      setDenyDialogOpen(false);
      setSelectedPickupId(null);
      await fetchPickupRequests();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to submit request",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Don't show section if no pending pickups
  if (loading) {
    return null;
  }

  if (pickups.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
        <Bell className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
          Action Required: Confirm Your Pickup
        </h2>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        A pickup date and time has been proposed for your packages. Please review and confirm or request a change.
      </p>

      <div className="space-y-4">
        {pickups.map((pickup) => (
          <PickupRequestCard
            key={pickup.id}
            pickup={pickup}
            packages={pickup.packages}
            remainingPickups={remainingPickups}
            maxPickups={maxPickups}
            onApprove={handleApprove}
            onDeny={handleDenyClick}
            loading={actionLoading}
          />
        ))}
      </div>

      <DenyPickupDialog
        open={denyDialogOpen}
        onOpenChange={setDenyDialogOpen}
        onConfirm={handleDenyConfirm}
        loading={actionLoading}
      />
    </div>
  );
};
